import { unwrap } from "@sagot/api-client";
import { RETENTION_PRESET_DAYS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, ErrorState, Icon, RadioGroup, Skeleton, Switch } from "@sagot/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceId } from "../../../../session/workspace-context";
import { CardActions, Gate, TableScroll, useCanManage } from "./panel-kit";
import { RetentionReviewDialog } from "./retention-review-dialog";
import { describeDays, hasPendingChange, type ClassState, type RetentionPreset, type Selection } from "./retention-presets-model";

const K = "privacyM29.retention";
type Choice = RetentionPreset | "custom";

/** `GET /v1/retention-presets` (M29 E46): the days and legal hold of each data class. */
function useRetentionPresets() {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: ["ws", wsId, "settings", "retention-presets"] as const, queryFn: () => unwrap(api.GET("/v1/retention-presets")) });
}

function Days({ days }: { days: number }) {
  const { t } = useTranslation();
  const parts = describeDays(days);
  return <>{t(`${K}.${parts.key}`, { count: parts.count })}</>;
}

function Header({ badge }: { badge?: Choice }) {
  const { t } = useTranslation();
  return (
    <div className="card-header flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h2 id="retention-presets-title" className="card-title">
          {t(`${K}.title`)}
        </h2>
        <p className="mt-1 text-xs text-fg-muted">{t(`${K}.intro`)}</p>
      </div>
      {badge ? (
        <Badge tone={badge === "custom" ? "neutral" : "success"} dot data-testid="retention-current">
          {badge === "custom" ? t(`${K}.custom`) : t(`${K}.presets.${badge}.name`)}
        </Badge>
      ) : null}
    </div>
  );
}

function ClassRow({ item, target, held, disabled, onHold }: { item: ClassState; target: number; held: boolean; disabled: boolean; onHold: (on: boolean) => void }) {
  const { t } = useTranslation();
  const name = t(`${K}.classes.${item.class}.name`);
  const fixed = item.min_days === item.max_days;
  return (
    <tr data-testid={`retention-row-${item.class}`}>
      <th scope="row" className="min-w-[140px] text-left">
        <div className="text-sm font-semibold">{name}</div>
        <div className="text-xs font-normal text-fg-muted">{t(`${K}.classes.${item.class}.hint`)}</div>
      </th>
      <td className="whitespace-nowrap tabular-nums">
        {target !== item.days ? (
          <span className="flex items-center gap-1.5" data-testid={`retention-target-${item.class}`}>
            <span className="text-fg-muted">
              <Days days={item.days} />
            </span>
            <Icon name="arrow-right" className="size-3.5 text-fg-subtle" />
            <strong className="font-semibold text-primary-ink">
              <Days days={target} />
            </strong>
          </span>
        ) : (
          <span data-testid={`retention-days-${item.class}`}>
            <Days days={item.days} />
          </span>
        )}
        {fixed ? <Badge tone="neutral" className="ml-2">{t(`${K}.fixed`)}</Badge> : null}
      </td>
      <td>
        <Switch checked={held} disabled={disabled} onCheckedChange={onHold} aria-label={t(`${K}.holdOn`, { name })} data-testid={`retention-hold-${item.class}`} />
      </td>
    </tr>
  );
}

interface PresetOption {
  value: Choice;
  label: string;
  description: string;
}

function presetOptions(current: RetentionPreset | null, t: (key: string) => string): PresetOption[] {
  const named = (["standard", "aml_5_years"] as const).map((key) => ({ value: key, label: t(`${K}.presets.${key}.name`), description: t(`${K}.presets.${key}.body`) }));
  return current === null ? [{ value: "custom", label: t(`${K}.custom`), description: t(`${K}.customBody`) }, ...named] : named;
}

/** The shared radio group (roving focus: the arrow keys move to and check the next preset, Tab leaves the group; 24px targets). */
function PresetPicker({ options, value, onChange, label }: { options: readonly PresetOption[]; value: Choice; onChange: (value: Choice) => void; label: string }) {
  return <RadioGroup aria-label={label} variant="cards" options={options} value={value} onValueChange={onChange} />;
}

function Loaded({ current, classes, floor }: { current: RetentionPreset | null; classes: readonly ClassState[]; floor: number | null }) {
  const { t } = useTranslation();
  const manage = useCanManage("settings.manage");
  const [choice, setChoice] = useState<Choice>(current ?? "custom");
  const [holds, setHolds] = useState<Selection["holds"]>({});
  const [reviewing, setReviewing] = useState(false);
  const selection: Selection = { preset: choice === "custom" ? null : choice, holds };
  const pending = hasPendingChange(classes, current, selection);
  const target = (item: ClassState) => (choice === "custom" ? item.days : RETENTION_PRESET_DAYS[choice][item.class]);
  const showFloor = floor !== null && choice === current;
  return (
    <section className="card" aria-labelledby="retention-presets-title" data-testid="retention-presets-card">
      <Header badge={current ?? "custom"} />
      <fieldset className="space-y-3 px-5 py-4" disabled={!manage.allowed}>
        <legend className="mb-2 text-sm font-semibold">{t(`${K}.presetLabel`)}</legend>
        <PresetPicker label={t(`${K}.presetLabel`)} options={presetOptions(current, t)} value={choice} onChange={setChoice} />
      </fieldset>
      <TableScroll label={t(`${K}.title`)}>
        <table className="table" data-testid="retention-table">
          <thead>
            <tr>
              <th scope="col">{t(`${K}.classLabel`)}</th>
              <th scope="col">{t(`${K}.keepLabel`)}</th>
              <th scope="col">{t(`${K}.holdLabel`)}</th>
            </tr>
          </thead>
          <tbody>
            {classes.map((item) => (
              <ClassRow
                key={item.class}
                item={item}
                target={target(item)}
                held={holds[item.class] ?? item.legal_hold}
                disabled={!manage.allowed}
                onHold={(on) => { setHolds((previous) => ({ ...previous, [item.class]: on })); }}
              />
            ))}
          </tbody>
        </table>
      </TableScroll>
      <div className="space-y-2 border-t border-line px-5 py-3 text-xs text-fg-muted">
        {showFloor ? (
          <p className="flex items-start gap-2" data-testid="retention-floor">
            <Icon name="shield-check" className="mt-0.5 size-4 shrink-0 text-primary-ink" />
            <span>{t(`${K}.floorNote`, { days: floor })}</span>
          </p>
        ) : null}
        <p className="flex items-start gap-2">
          <Icon name="info" className="mt-0.5 size-4 shrink-0 text-primary-ink" />
          <span>{t(`${K}.auditNote`)}</span>
        </p>
        {!manage.allowed ? <p>{t(`${K}.needsManage`)}</p> : null}
      </div>
      <CardActions>
        <Gate reason={manage.reason}>
          <Button disabled={!manage.allowed || !pending} onClick={() => { setReviewing(true); }}>
            <Icon name="list-checks" />
            {t(`${K}.review`)}
          </Button>
        </Gate>
      </CardActions>
      {reviewing ? <RetentionReviewDialog classes={classes} selection={selection} onClose={() => { setReviewing(false); }} /> : null}
    </section>
  );
}

/** Settings > Privacy > "Retention by data class" (privacy-security pin 5 area, SEC-007): presets with a diff before they apply, and a legal hold per class. */
export function RetentionPresetsCard() {
  const { t } = useTranslation();
  const presets = useRetentionPresets();
  if (presets.isPending) {
    return (
      <section className="card" aria-labelledby="retention-presets-title" aria-busy="true" data-testid="retention-presets-card">
        <Header />
        <div className="space-y-3 px-5 py-4" role="status" aria-label={t("common.loading")}>
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </section>
    );
  }
  if (presets.isError) {
    return (
      <section className="card" aria-labelledby="retention-presets-title" data-testid="retention-presets-card">
        <Header />
        <ErrorState headingLevel={3} title={t(`${K}.failedTitle`)} description={t(`${K}.failedBody`)} onRetry={() => void presets.refetch()} retryLabel={t("common.retry")} />
      </section>
    );
  }
  const { preset, classes, chats_floor_days: floor } = presets.data;
  return <Loaded key={presets.dataUpdatedAt} current={preset} classes={classes} floor={floor} />;
}
